/** English fallback for the WhatsApp integration; wired up in lib/i18n/en.ts. */
export const whatsAppEn = {
  qr: {
    contactMismatch: 'The phone number does not belong to the selected customer or contact.',
    history: 'WhatsApp message',
    accessDenied: 'The WhatsApp connection is not enabled on this server or you lack permission.',
    sendFailed:
      'The message could not be sent with certainty. Check its status and the connection before sending again.',
    documentCustomerMismatch: 'The document does not belong to this conversation’s customer.',
    templatesUnavailable: 'Cloud templates are not available for WhatsApp (Baileys).',
    provider: 'WhatsApp (Baileys)',
    providerField: 'Connection provider',
    cloudProvider: 'WhatsApp Business Cloud API',
    label: 'Unofficial connection using Baileys',
    help: 'WhatsApp help',
    setup:
      'Link your own WhatsApp account by QR code. Text messages to known ERP contacts appear even when the conversation starts on the phone; replies and messages received during an interruption are also imported.',
    loadError: 'The connection status could not be loaded.',
    connect: 'Connect number',
    reconnect: 'Reconnect',
    logout: 'Log out',
    qrAlt: 'QR code for linking WhatsApp',
    scan: 'Open WhatsApp on your phone → Linked devices → Link a device and scan this QR code.',
    expires: 'QR code valid until {{time}}.',
    expired: 'The QR code has expired. Reconnect to request a new code.',
    connectionError:
      'The connection was interrupted or could not be established. Reconnect to try again.',
    actionError: 'The action failed. Check your server access and try again.',
    limits:
      'Individual sending requires an explicit user action. Private chat history and the address book are not imported. Introduction mode, consent and contact restrictions still apply.',
    logoutTitle: 'Log out the WhatsApp number?',
    logoutDescription:
      '{{phone}} will be disconnected and the saved session deleted. A new connection requires scanning the QR code again.',
    logoutConfirm: 'Log out number and delete session',
    pdfHint:
      'The document is sent directly as a PDF using WhatsApp (Baileys). Cloud templates and the 24-hour window do not apply here.',
    pdfSend: 'Send PDF using WhatsApp',
    accepted: 'Sending was accepted. Delivery status follows actual WhatsApp events.',
    status: {
      DISCONNECTED: 'Disconnected',
      CONNECTING: 'Connecting',
      QR_READY: 'QR code ready to scan',
      CONNECTED: 'Connected',
      EXPIRED: 'QR code expired',
      ERROR: 'Connection error',
    },
  },
  inbox: {
    title: 'WhatsApp team inbox',
    subtitle: 'Customer messages and PDF documents using the available WhatsApp connection.',
    search: 'Search name, number or message',
    empty: 'No conversations in this view.',
    error: 'The conversations could not be loaded.',
    selectHint: 'Select a conversation on the left.',
  },
  scope: {
    ALL: 'All',
    MINE: 'Mine',
    UNASSIGNED: 'Unassigned',
    CLOSED: 'Closed',
  },
  conversationStatus: {
    OPEN: 'Open',
    PENDING: 'Pending',
    RESOLVED: 'Resolved',
    ARCHIVED: 'Archived',
  },
  status: {
    PENDING: 'Sending',
    SENT: 'Sent',
    DELIVERED: 'Delivered',
    READ: 'Read',
    FAILED: 'Failed',
  },
  templateStatus: {
    APPROVED: 'Approved',
    PENDING: 'In review',
    REJECTED: 'Rejected',
    PAUSED: 'Paused',
    DISABLED: 'Disabled',
  },
  category: {
    MARKETING: 'Marketing',
    UTILITY: 'Utility',
    AUTHENTICATION: 'Authentication',
  },
  optIn: {
    OPTED_IN: 'Consent on file',
    OPTED_OUT: 'Objection recorded',
    PENDING: 'Request pending',
    NOT_REQUESTED: 'Not requested yet',
  },
  window: {
    open: 'Window open · {{hours}} h {{minutes}} min',
    openShort: 'Window open ({{hours}} h {{minutes}} min)',
    closed: 'Templates only',
    closedShort: 'Window closed — templates only',
  },
  chat: {
    unknownCustomer: 'No customer linked',
    openCustomer: 'Customer profile',
    assignee: 'Assignee',
    unassigned: 'Unassigned',
    status: 'Status',
    empty: 'No messages in this conversation yet.',
    error: 'The message history could not be loaded.',
    placeholder: 'Write a message … (Enter sends, Shift+Enter for a new line)',
    send: 'Send',
    emoji: 'Insert emoji',
    attachDocument: 'Attach document',
    useTemplate: 'Use template',
    download: 'Download',
    attachment: 'Attachment',
    templateLabel: 'Template: {{name}}',
    windowClosed: 'The 24h window has expired. Send a template to reopen the conversation.',
    chooseTemplate: 'Choose template',
  },
  sidebar: {
    title: 'Customer file',
    placeholder: 'Select a conversation to see the customer details.',
    noCustomer: 'This number is not linked to a customer.',
    contact: 'Contact: {{name}}',
    optIn: 'WhatsApp opt-in confirmed',
    optInSaved: 'Consent saved.',
    openDocuments: 'Open documents',
    noDocuments: 'No open documents.',
    documentsError: 'The documents could not be loaded.',
  },
  templates: {
    pickTitle: 'Send template',
    pickDescription: 'Only templates approved by Meta can be delivered outside the 24-hour window.',
    template: 'Template',
    choose: 'Choose template',
    placeholder: 'Placeholder {{position}}',
    preview: 'Preview',
    pdfHeader: 'Document PDF as header',
    send: 'Send template',
    sent: 'The template has been sent.',
    error: 'The templates could not be loaded.',
    emptyApproved: 'No approved template is available.',
  },
  document: {
    title: 'Attach document',
    description: 'The document is sent as a PDF to the current conversation.',
    document: 'Document',
    choose: 'Choose document',
    caption: 'Caption',
    captionPlaceholder: 'Optional text for the attachment',
    send: 'Send document',
    sent: 'The document has been sent.',
    empty: 'There is no document for this customer.',
    error: 'The documents could not be loaded.',
  },
  documentSend: {
    chooseConversation: 'Choose a conversation',
    action: 'Send via WhatsApp',
    title: 'Send document via WhatsApp',
    description: 'Deliver document {{number}} to {{customer}}.',
    noConversation: 'There is no conversation for this customer yet.',
    createChat: 'Create conversation',
    windowOpenHint: 'The service window is open; the template delivers the document with its PDF.',
    windowClosedHint: 'The service window is closed. Delivery uses an approved template.',
    chooseTemplate: 'Choose template & send',
  },
  customerTab: {
    subtitle:
      'Stored WhatsApp conversations for this customer. Only accounts assigned to you are shown.',
    start: 'New conversation',
    startTitle: 'Start a WhatsApp conversation',
    startDescription: 'The number is normalised to E.164 automatically.',
    started: 'The conversation has been created.',
    phone: 'Mobile number',
    empty: 'There is no WhatsApp conversation for this customer yet.',
    error: 'The conversations could not be loaded.',
    noAccess: 'You lack the permission to view the WhatsApp history.',
  },
  config: {
    title: 'WhatsApp configuration',
    subtitle: 'Manage WhatsApp connections and Cloud templates.',
    error: 'The configuration could not be loaded.',
    tabConnection: 'WhatsApp Business Cloud API',
    tabTemplates: 'Cloud templates',
    credentials: 'Meta Graph API',
    credentialsHint:
      'These values come from the Meta developer dashboard. Secrets are stored encrypted and never returned.',
    phoneNumberId: 'Phone number ID',
    wabaId: 'WhatsApp business account ID',
    appId: 'App ID',
    businessPhoneNumber: 'Sender number (E.164)',
    displayName: 'Display name',
    accessToken: 'Permanent access token',
    appSecret: 'App secret',
    secretPlaceholder: 'Fill in only to replace the value',
    secretSet: 'Stored ({{hint}})',
    secretMissing: 'Not stored yet',
    rotateToken: 'Generate a new verify token',
    rotateTokenHint: 'Requires setting up the webhook again in the Meta dashboard.',
    saved: 'The configuration has been saved.',
    test: 'Test connection',
    verifiedName: 'Verified name',
    displayNumber: 'Display number',
    quality: 'Quality',
    limit: 'Messaging limit',
    checkedAt: 'Checked at {{timestamp}}',
    webhook: 'Webhook setup',
    webhookHint:
      'Enter these values under "Webhooks" in the Meta developer dashboard and subscribe to the "messages" field.',
    callbackUrl: 'Callback URL',
    verifyToken: 'Verify token',
    copy: 'Copy',
    copied: 'Copied to the clipboard.',
    lastWebhook: 'Last delivery: {{timestamp}}',
    noWebhookYet: 'No delivery received yet.',
    templates: 'Templates from Meta',
    templatesHint: 'The local mirror is fully refreshed on each sync.',
    templatesEmpty: 'No templates have been synchronised yet.',
    sync: 'Sync templates',
    syncDone: '{{fetched}} templates fetched · {{created}} new · {{updated}} updated.',
    templateName: 'Name',
    language: 'Language',
    category: 'Category',
    status: 'Status',
    templatePreview: 'Preview with ERP placeholders',
  },
  accounts: {
    unavailableMember: 'Employee no longer available ({{id}})',
    unavailableHelp:
      'These saved assignments belong to employees who are no longer available. Remove them explicitly if they should be revoked. No assignment is removed automatically.',

    title: 'WhatsApp accounts',
    description:
      'Each account has its own connection. Only assigned employees can see its chats and QR code.',
    select: 'WhatsApp account',
    choose: 'Choose an account',
    all: 'All assigned accounts',
    empty: 'No WhatsApp account is assigned to you.',
    error: 'WhatsApp accounts could not be loaded.',
    new: 'Add account',
    edit: 'Edit account',
    label: 'Account name',
    members: 'Assigned employees',
    noMembers: 'Without assigned employees, nobody can use this account.',
    manageEmpty: 'No WhatsApp account has been added yet.',
    saved: 'The WhatsApp account was saved.',
    saveError: 'The account could not be saved. Check the values and try again.',
    rescan: 'The pairing was lost. Scan a new QR code.',
    membersError: 'Employees could not be loaded.',
    invalid: 'Check the account name and employee selection.',
  },
  history: {
    connect: 'Connect and load history',
    connectTitle: 'Link {{account}} with text history?',
    connectDescription:
      'Choose this action before the first QR scan to request available text history while pairing. An existing link is not logged out automatically.',
    connectConfirm: 'Link with text history',
    pairingRequired:
      'This link has no suitable ERP messages to use as a starting point. Deliberately log out the account, then choose “Connect and load history” before scanning the new QR code. Normal reconnection does not retrieve this history.',

    action: 'Request history',
    title: 'Request history for {{account}}?',
    description:
      'Requests available text messages from the linked device. Only known ERP customers and contacts or explicitly assigned account chats are imported. Older messages load in batches: up to 200 chats, 1,000 messages per chat and ten minutes per request. Request again for longer histories. WhatsApp determines availability; a complete import is not guaranteed.',
    confirm: 'Request history now',
    pending: 'Requesting history …',
    requested:
      'The request was submitted. Available text messages from known ERP contacts will appear after processing.',
    unavailable: 'The linked device is not providing history at present.',
    error: 'History could not be requested. Check the connection and try again.',
  },
  phone: {
    change: 'Change number',
    changeTitle: 'Change WhatsApp number',
    changeDescription:
      'The new number gets a separate conversation on the selected WhatsApp account. Existing history is preserved. Customer details are not changed automatically.',
    useNumber: 'Use new number',

    existing: 'Existing number',
    choose: 'Choose a customer or contact number',
    invalid: 'Enter a valid mobile number, for example 0170 1234567 or +49 170 1234567.',
    loadError: 'Existing phone numbers could not be loaded.',
    source: {
      CUSTOMER: 'Customer number',
      CONTACT_MOBILE: 'Contact: mobile number',
      CONTACT_PHONE: 'Contact: phone number',
    },
  },
  baileysTemplates: {
    title: 'Local WhatsApp templates',
    description:
      'Reusable text for Baileys accounts. Local templates need no Meta approval and follow the usual contact preferences.',
    create: 'Add local template',
    edit: 'Edit local template',
    delete: 'Delete local template',
    confirmDeleteTitle: 'Delete {{name}}?',
    confirmDeleteDescription:
      'This local template will no longer be available for new messages. Existing messages remain available.',
    empty: 'No local templates have been added yet.',
    loadError: 'Local templates could not be loaded.',
    name: 'Template name',
    language: 'Language',
    body: 'Message text',
    bodyHelp:
      'Use numbered placeholders such as {{example}}. Start at 1 without gaps; up to 50 different placeholders and 4,096 characters.',
    variableMapping: 'Optional ERP values',
    mappingSource: 'Value source {{position}}',
    mappingLabel: 'Field label {{position}}',
    noMapping: 'Enter manually',
    transactionalOnly:
      'For transactional messages. Marketing communication requires the separate consent-based marketing process.',
    saved: 'The local template was saved.',
    deleted: 'The local template was deleted.',
    versionConflict:
      'This template was changed by someone else. Reload it before saving or sending.',
    duplicateName: 'A local template with this name and language already exists.',
    invalidPlaceholders:
      'Use only numbered placeholders from 1 to 50 without gaps, such as {{example}}.',
    invalidMapping: 'Assign each ERP field only once and only to an existing placeholder.',
    invalidParameters: 'Fill every placeholder once with a valid value. Check the message length.',
    invalidName: 'Enter a template name with at most 120 characters.',
    invalidLanguage: 'Choose a supported language.',
    invalidBody: 'Enter a message with at most 4,096 characters.',
    validationSummary: 'The template could not be saved. Check the marked fields.',
    discardTitle: 'Discard changes?',
    discardDescription: 'Your unsaved changes to this template will be lost.',
    discard: 'Discard changes',
    pickTitle: 'Send a local WhatsApp template',
    pickDescription:
      'Check the text and all values before sending from the selected Baileys account.',
    choose: 'Choose local template',
    send: 'Send message',
    preview: 'Message preview',
    pdfAttachment: 'PDF attached',
    search: 'Search local templates',
    page: 'Page {{page}} of {{pages}}',
    captionTooLong: 'A message accompanying a PDF can contain at most 1,024 characters.',
    sources: {
      'customer.name': 'Customer name',
      'customer.number': 'Customer number',
      'contact.firstName': 'Contact first name',
      'document.number': 'Document number',
      'document.date': 'Document date',
      'document.dueDate': 'Due date',
      'document.grossTotal': 'Gross total',
      'document.currency': 'Currency',
      'tenant.name': 'Company name',
      'user.name': 'Employee name',
      custom: 'Manual value',
    },
  },
};
